import { CommonBasesType } from 'components/SearchModal/types'

import { Currency, CurrencyAmount, Percent } from '@pancakeswap/sdk'
import {
  AutoColumn,
  Button,
  RowBetween,
  Text,
  AutoRow,
  Box,
  NumericalInput,
  ConfirmationModalContent,
  useModal,
  Message,
  MessageText,
  PreTitle,
  DynamicSection,
  Flex,
  Toggle,
} from '@pancakeswap/uikit'
import { logGTMClickAddLiquidityEvent } from 'utils/customGTMEventTracking'

import useV3DerivedInfo from 'hooks/v3/useV3DerivedInfo'
import { FeeAmount, NonfungiblePositionManager, Position } from '@pancakeswap/v3-sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { useUserSlippage, useIsExpertMode } from '@pancakeswap/utils/user'

import { maxAmountSpend } from 'utils/maxAmountSpend'
import { basisPointsToPercent } from 'utils/exchange'
import { Field } from 'state/mint/actions'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'

import { useTransactionAdder } from 'state/transactions/hooks'
import { useV3NFTPositionManagerContract } from 'hooks/useContract'
import { useRouter } from 'next/router'
import { useIsTransactionUnsupported, useIsTransactionWarning } from 'hooks/Trades'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTranslation } from '@pancakeswap/localization'
import { useSendTransaction, useWalletClient } from 'wagmi'
import styled from 'styled-components'
import LiquidityChartRangeInput from 'components/LiquidityChartRangeInput'
import TransactionConfirmationModal from 'components/TransactionConfirmationModal'
import { Bound } from 'config/constants/types'
import { V3SubmitButton } from 'views/AddLiquidityV3/components/V3SubmitButton'
import { formatCurrencyAmount, formatRawAmount } from 'utils/formatCurrencyAmount'
import { QUICK_ACTION_CONFIGS } from 'views/AddLiquidityV3/types'
import { isUserRejected } from 'utils/sentry'
import { hexToBigInt } from 'viem'
import { getViemClients } from 'utils/viem'
import { calculateGasMargin } from 'utils'

import { ZoomLevels, ZOOM_LEVELS } from 'components/LiquidityChartRangeInput/types'
import { useV3AutoSwapCalculation } from 'hooks/v3/useV3AutoSwapCalculation'
import { useV3AutoSwapExecution } from 'hooks/v3/useV3AutoSwapExecution'
import { useV3MaxAddableLiquidity } from 'hooks/v3/useV3MaxAddableLiquidity'
import { usePriceAPI } from 'hooks/usePriceAPI'
import { waitForTransaction } from 'wagmi/actions'
import RangeSelector from './components/RangeSelector'
import { PositionPreview } from './components/PositionPreview'
import RateToggle from './components/RateToggle'
import LockedDeposit from './components/LockedDeposit'
import { AutoSwapPreview } from './components/AutoSwapPreview'
import { SwapTradeDetails } from './components/SwapTradeDetails'
import { useRangeHopCallbacks } from './form/hooks/useRangeHopCallbacks'
import { useV3MintActionHandlers } from './form/hooks/useV3MintActionHandlers'
import { useV3FormAddLiquidityCallback, useV3FormState } from './form/reducer'
import CapitalEfficiencyWarning from './components/ErrorsMessages/showCapitalEfficiencyWarning'
import NoLiquidityMessage from './components/ErrorsMessages/noLiquidity'

const StyledInput = styled(NumericalInput)`
  background-color: ${({ theme }) => theme.colors.input};
  box-shadow: ${({ theme, error }) => theme.shadows[error ? 'warning' : 'inset']};
  border-radius: 16px;
  padding: 8px 16px;
  font-size: 16px;
  width: 100%;
  margin-bottom: 16px;
`

export const HideMedium = styled.div`
  ${({ theme }) => theme.mediaQueries.md} {
    display: none;
  }
`

export const MediumOnly = styled.div`
  display: none;
  ${({ theme }) => theme.mediaQueries.md} {
    display: initial;
  }
`

export const RightContainer = styled(AutoColumn)`
  height: fit-content;

  grid-row: 2 / 3;
  grid-column: 1;

  ${({ theme }) => theme.mediaQueries.md} {
    grid-row: 1 / 3;
    grid-column: 2;
  }
`

interface V3FormViewPropsType {
  baseCurrency: Currency
  quoteCurrency: Currency
  currencyIdA: string
  currencyIdB: string
  feeAmount: number
}

export default function V3FormView({
  feeAmount,
  baseCurrency,
  quoteCurrency,
  currencyIdA,
  currencyIdB,
}: V3FormViewPropsType) {
  const router = useRouter()
  const { data: signer } = useWalletClient()
  const { sendTransactionAsync } = useSendTransaction()
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm

  const {
    t,
    currentLanguage: { locale },
  } = useTranslation()
  const expertMode = useIsExpertMode()

  const positionManager = useV3NFTPositionManagerContract()
  const { account, chainId, isWrongNetwork } = useActiveWeb3React()
  const addTransaction = useTransactionAdder()

  // mint state
  const formState = useV3FormState()
  const { independentField, typedValue, startPriceTypedValue } = formState

  const {
    pool,
    ticks,
    dependentField,
    price,
    pricesAtTicks,
    parsedAmounts,
    currencyBalances,
    position,
    noLiquidity,
    currencies,
    errorMessage,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    ticksAtLimit,
  } = useV3DerivedInfo(
    baseCurrency ?? undefined,
    quoteCurrency ?? undefined,
    feeAmount,
    baseCurrency ?? undefined,
    undefined,
    formState,
  )
  const { onFieldAInput, onFieldBInput, onLeftRangeInput, onRightRangeInput, onStartPriceInput, onBothRangeInput } =
    useV3MintActionHandlers(noLiquidity)

  // modal and loading
  // capital efficiency warning
  const [showCapitalEfficiencyWarning, setShowCapitalEfficiencyWarning] = useState<boolean>(false)

  useEffect(() => {
    setShowCapitalEfficiencyWarning(false)
  }, [baseCurrency, quoteCurrency, feeAmount])

  useEffect(() => {
    if (feeAmount) {
      setActiveQuickAction(undefined)
      onBothRangeInput({
        leftTypedValue: '',
        rightTypedValue: '',
      })
    }
    // NOTE: ignore exhaustive-deps to avoid infinite re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeAmount])

  const onAddLiquidityCallback = useV3FormAddLiquidityCallback()

  // txn values
  const deadline = useTransactionDeadline() // custom from users settings
  const [txHash, setTxHash] = useState<string>('')
  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const nftPositionManagerAddress = useV3NFTPositionManagerContract()?.address
  // check whether the user has approved the router on the tokens
  const [approvalA, approveACallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_A], nftPositionManagerAddress)
  const [approvalB, approveBCallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_B], nftPositionManagerAddress)

  const [allowedSlippage] = useUserSlippage() // custom from users

  const [autoSwap, setAutoSwap] = useState<boolean>(false)

  // Fetch token prices from external API
  const { data: tokenPrices } = usePriceAPI()

  // Calculate max addable liquidity considering pool ratio
  // const maxAddableLiquidity = useV3MaxAddableLiquidity({
  //   enabled: autoSwap,
  //   pool,
  //   tickLower: ticks[Bound.LOWER],
  //   tickUpper: ticks[Bound.UPPER],
  //   currencyBalances,
  //   tokenPrices: tokenPrices || null,
  //   depositADisabled,
  //   depositBDisabled,
  // })

  // Auto-swap calculation
  const autoSwapCalculation = useV3AutoSwapCalculation({
    enabled: autoSwap,
    pool,
    tickLower: ticks[Bound.LOWER],
    tickUpper: ticks[Bound.UPPER],
    inputCurrency: currencies[independentField],
    inputAmount: typedValue,
    outputCurrency: currencies[dependentField],
    outOfRange,
    depositADisabled,
    depositBDisabled,
    currencyBalances,
    tokenPrices: tokenPrices || null,
  })

  // Auto-swap execution - uses the same swap logic as the swap page
  const {
    swapRouterAddress,
    executeSwap,
    isExecuting: isSwapExecuting,
    trade: swapTrade,
    tradeLoading: swapTradeLoading,
    tradeError: swapTradeError,
  } = useV3AutoSwapExecution({
    account: account as `0x${string}` | undefined,
    chainId,
    swapFromCurrency: autoSwapCalculation.needsSwap ? autoSwapCalculation.swapFromCurrency : undefined,
    swapToCurrency: autoSwapCalculation.needsSwap ? autoSwapCalculation.swapToCurrency : undefined,
    swapAmount: autoSwapCalculation.needsSwap ? autoSwapCalculation.swapAmount : undefined,
    enabled: autoSwap && autoSwapCalculation.needsSwap,
  })

  // Check for swap liquidity error
  const swapLiquidityError = useMemo(() => {
    // Only check when auto-swap is enabled and a swap is needed
    if (!autoSwap || !autoSwapCalculation.needsSwap) {
      return undefined
    }

    // If trade is still loading, don't show error yet
    if (swapTradeLoading) {
      return undefined
    }

    // If there's a trade error, show it
    if (swapTradeError) {
      return t('Unable to find swap route')
    }

    // If there's no trade route after loading, show error
    if (!swapTrade && !swapTradeLoading) {
      return t('Insufficient liquidity for swap. Please reduce deposit amounts.')
    }

    return undefined
  }, [autoSwap, autoSwapCalculation.needsSwap, swapTrade, swapTradeLoading, swapTradeError, t])

  // Override error message when auto-swap is enabled
  // With auto-swap, we only need to check the INPUT token balance (the one being swapped FROM)
  // The OUTPUT token balance will be obtained through the swap
  const customErrorMessage = useMemo(() => {
    // If auto-swap is disabled, use the original error message
    if (!autoSwap) {
      return errorMessage
    }

    // If auto-swap is enabled and will perform a swap, check only the INPUT token balance
    if (autoSwap && autoSwapCalculation.needsSwap) {
      // First priority: Check for swap liquidity errors
      if (swapLiquidityError) {
        return swapLiquidityError
      }

      const inputField = independentField
      const inputCurrency = currencies[inputField]
      const inputAmount = parsedAmounts[inputField]
      const inputBalance = currencyBalances[inputField]

      // Check if we have enough of the INPUT token
      // This includes both the deposit amount AND the swap amount
      if (inputAmount && inputCurrency) {
        const { swapAmount } = autoSwapCalculation
        const totalNeeded = swapAmount ? inputAmount.add(swapAmount) : inputAmount

        if (!inputBalance || inputBalance.lessThan(totalNeeded)) {
          return t('Insufficient %symbol% balance', { symbol: inputCurrency.symbol })
        }
      }

      // Allow other errors to pass through (invalid range, invalid price, etc.)
      // But filter out the "Insufficient balance" error for the OUTPUT token
      if (errorMessage) {
        const outputCurrency = currencies[dependentField]

        // Convert errorMessage to string for pattern matching
        const errorString = String(errorMessage)
        const insufficientOutputPattern = new RegExp(`Insufficient.*${outputCurrency?.symbol}.*balance`, 'i')

        if (insufficientOutputPattern.test(errorString)) {
          // This is an "insufficient output token" error - ignore it when auto-swap is enabled
          return undefined
        }
      }

      // For all other errors, return the original error message
      return errorMessage
    }

    // Auto-swap enabled but no swap needed - use original error
    return errorMessage
  }, [
    autoSwap,
    autoSwapCalculation.needsSwap,
    autoSwapCalculation.swapAmount,
    errorMessage,
    independentField,
    dependentField,
    currencies,
    parsedAmounts,
    currencyBalances,
    swapLiquidityError,
    t,
  ])

  // Update isValid to use custom error message
  const isValid = !customErrorMessage && !invalidRange

  const maxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = useMemo(
    () =>
      [Field.CURRENCY_A, Field.CURRENCY_B].reduce((accumulator, field) => {
        return {
          ...accumulator,
          [field]: maxAmountSpend(currencyBalances[field]),
        }
      }, {}),
    [currencyBalances],
  )

  // Additional approval for swap router if auto-swap is needed
  const [approvalSwap, approveSwapCallback] = useApproveCallback(
    autoSwap && autoSwapCalculation.needsSwap ? autoSwapCalculation.swapAmount : undefined,
    swapRouterAddress,
  )

  // Separate swap execution function
  const onSwap = useCallback(async (): Promise<void> => {
    if (!autoSwap || !autoSwapCalculation.needsSwap) {
      console.info('Auto-swap is not enabled or not needed')
      return
    }

    if (!swapTrade) {
      throw new Error('No swap route available')
    }

    try {
      const swapResult = await executeSwap()

      if (!swapResult) {
        throw new Error('Swap execution failed')
      }

      // Wait for swap transaction to be confirmed
      await waitForTransaction({
        hash: swapResult.hash,
        chainId,
      })
    } catch (error) {
      console.error('❌ Swap error:', error)
      throw error
    }
  }, [autoSwap, autoSwapCalculation.needsSwap, swapTrade, executeSwap, chainId])

  const onAdd = useCallback(async () => {
    if (
      !chainId ||
      !signer ||
      !account ||
      !nftPositionManagerAddress ||
      !positionManager ||
      !baseCurrency ||
      !quoteCurrency
    )
      return

    if (position && account && deadline) {
      const useNative = baseCurrency.isNative ? baseCurrency : quoteCurrency.isNative ? quoteCurrency : undefined

      const { calldata, value } = NonfungiblePositionManager.addCallParameters(position, {
        slippageTolerance: basisPointsToPercent(allowedSlippage),
        recipient: account,
        deadline: deadline.toString(),
        useNative,
        createPool: noLiquidity,
      })

      setAttemptingTxn(true)
      const txn = {
        data: calldata,
        to: nftPositionManagerAddress,
        value: hexToBigInt(value),
        account,
      }
      getViemClients({ chainId })
        .estimateGas(txn)
        .then((gas) => {
          sendTransactionAsync({
            ...txn,
            gas: calculateGasMargin(gas),
          })
            .then((response) => {
              const baseAmount = formatRawAmount(
                parsedAmounts[Field.CURRENCY_A]?.quotient?.toString() ?? '0',
                baseCurrency.decimals,
                4,
              )
              const quoteAmount = formatRawAmount(
                parsedAmounts[Field.CURRENCY_B]?.quotient?.toString() ?? '0',
                quoteCurrency.decimals,
                4,
              )

              setAttemptingTxn(false)
              addTransaction(response, {
                type: 'add-liquidity-v3',
                summary: `Add ${baseAmount} ${baseCurrency?.symbol} and ${quoteAmount} ${quoteCurrency?.symbol}`,
              })
              setTxHash(response.hash)
              onAddLiquidityCallback(response.hash)
            })
            .catch((error) => {
              console.error('Failed to send transaction', error)
              setAttemptingTxn(false)
              // we only care if the error is something _other_ than the user rejected the tx
              if (!isUserRejected(error)) {
                console.error(error)
              }
            })
        })
    }
  }, [
    account,
    addTransaction,
    allowedSlippage,
    baseCurrency,
    chainId,
    deadline,
    nftPositionManagerAddress,
    noLiquidity,
    onAddLiquidityCallback,
    parsedAmounts,
    position,
    positionManager,
    quoteCurrency,
    sendTransactionAsync,
    signer,
  ])

  const handleDismissConfirmation = useCallback(() => {
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
    }
    setTxHash('')
  }, [onFieldAInput, txHash])
  const addIsUnsupported = useIsTransactionUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  // get value and prices at ticks
  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks
  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } = pricesAtTicks
  const { getDecrementLower, getIncrementLower, getDecrementUpper, getIncrementUpper, getSetFullRange } =
    useRangeHopCallbacks(baseCurrency ?? undefined, quoteCurrency ?? undefined, feeAmount, tickLower, tickUpper, pool)
  // we need an existence check on parsed amounts for single-asset deposits
  const showApprovalA = approvalA !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_A]
  const showApprovalB = approvalB !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_B]
  const showApprovalSwap = autoSwap && autoSwapCalculation.needsSwap && approvalSwap !== ApprovalState.APPROVED

  const translationData = useMemo(
    () => ({
      amountA: !depositADisabled ? formatCurrencyAmount(parsedAmounts[Field.CURRENCY_A], 4, locale) : '',
      symbolA: !depositADisabled ? currencies[Field.CURRENCY_A]?.symbol : '',
      amountB: !depositBDisabled ? formatCurrencyAmount(parsedAmounts[Field.CURRENCY_B], 4, locale) : '',
      symbolB: !depositBDisabled ? currencies[Field.CURRENCY_B]?.symbol : '',
    }),
    [depositADisabled, depositBDisabled, parsedAmounts, locale, currencies],
  )

  const pendingText = useMemo(
    () =>
      !outOfRange
        ? t('Supplying %amountA% %symbolA% and %amountB% %symbolB%', translationData)
        : t('Supplying %amountA% %symbolA% %amountB% %symbolB%', translationData),
    [t, outOfRange, translationData],
  )

  const [activeQuickAction, setActiveQuickAction] = useState<number>()

  const [onPresentAddLiquidityModal] = useModal(
    <TransactionConfirmationModal
      minWidth={['100%', , '420px']}
      title={t('Add Liquidity')}
      customOnDismiss={handleDismissConfirmation}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      content={() => (
        <ConfirmationModalContent
          topContent={() =>
            position ? (
              <PositionPreview
                position={position}
                inRange={!outOfRange}
                ticksAtLimit={ticksAtLimit}
                baseCurrencyDefault={baseCurrency}
              />
            ) : null
          }
          bottomContent={() => (
            <Button width="100%" mt="16px" onClick={onAdd}>
              {t('Add')}
            </Button>
          )}
        />
      )}
      pendingText={pendingText}
    />,
    true,
    true,
    'TransactionConfirmationModal',
  )

  const addIsWarning = useIsTransactionWarning(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  const handleButtonSubmit = useCallback(() => {
    // eslint-disable-next-line no-unused-expressions
    expertMode ? onAdd() : onPresentAddLiquidityModal()
    logGTMClickAddLiquidityEvent()
  }, [expertMode, onAdd, onPresentAddLiquidityModal])

  const buttons = (
    <V3SubmitButton
      addIsUnsupported={addIsUnsupported}
      addIsWarning={addIsWarning}
      account={account}
      isWrongNetwork={isWrongNetwork}
      approvalA={approvalA}
      approvalB={approvalB}
      isValid={isValid}
      showApprovalA={showApprovalA}
      approveACallback={approveACallback}
      currencies={currencies}
      approveBCallback={approveBCallback}
      showApprovalB={showApprovalB}
      parsedAmounts={parsedAmounts}
      onClick={handleButtonSubmit}
      attemptingTxn={attemptingTxn || isSwapExecuting}
      errorMessage={customErrorMessage}
      buttonText={t('Add')}
      depositADisabled={depositADisabled}
      depositBDisabled={depositBDisabled}
      showApprovalSwap={showApprovalSwap}
      approveSwapCallback={approveSwapCallback}
      swapCurrency={autoSwapCalculation.swapFromCurrency}
      autoSwapError={autoSwapCalculation.error || swapLiquidityError}
      onSwapClick={onSwap}
      needsSwap={
        autoSwap && autoSwapCalculation.needsSwap && !autoSwapCalculation.error && !swapLiquidityError && !!swapTrade
      }
    />
  )
  const handleAutoSwapChange = useCallback(() => {
    setAutoSwap(!autoSwap)
  }, [autoSwap])
  const handleRefresh = useCallback(
    (zoomLevel?: ZoomLevels) => {
      setActiveQuickAction(undefined)
      const currentPrice = price ? parseFloat((invertPrice ? price.invert() : price).toSignificant(8)) : undefined
      if (currentPrice) {
        onBothRangeInput({
          leftTypedValue: (
            currentPrice * zoomLevel?.initialMin || ZOOM_LEVELS[feeAmount ?? FeeAmount.MEDIUM].initialMin
          ).toString(),
          rightTypedValue: (
            currentPrice * zoomLevel?.initialMax || ZOOM_LEVELS[feeAmount ?? FeeAmount.MEDIUM].initialMax
          ).toString(),
        })
      }
    },
    [price, feeAmount, invertPrice, onBothRangeInput],
  )

  return (
    <>
      <DynamicSection
        style={{
          gridAutoRows: 'max-content',
          gridAutoColumns: '100%',
        }}
        disabled={!feeAmount || invalidPool || (noLiquidity && !startPriceTypedValue) || (!priceLower && !priceUpper)}
      >
        <PreTitle mb="8px">
          <RowBetween>
            <Box>{t('Deposit Amount')}</Box>
            {pool && (
              <AutoRow gap="8px" alignItems="center">
                <Text fontSize="14px">{t('Auto Swap')}</Text>
                <Toggle
                  checked={autoSwap}
                  onChange={handleAutoSwapChange}
                  scale="sm"
                  defaultColor="textDisabled"
                  checkedColor="success"
                />
              </AutoRow>
            )}
          </RowBetween>
        </PreTitle>

        <LockedDeposit locked={depositADisabled} mb="8px">
          <Box mb="8px">
            <CurrencyInputPanel
              showUSDPrice
              maxAmount={maxAmounts[Field.CURRENCY_A]}
              onMax={() => onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')}
              onPercentInput={(percent) =>
                onFieldAInput(maxAmounts[Field.CURRENCY_A]?.multiply(new Percent(percent, 100)).toExact() ?? '')
              }
              disableCurrencySelect
              value={formattedAmounts[Field.CURRENCY_A]}
              onUserInput={onFieldAInput}
              showQuickInputButton
              showMaxButton
              currency={currencies[Field.CURRENCY_A]}
              id="add-liquidity-input-tokena"
              showCommonBases
              commonBasesType={CommonBasesType.LIQUIDITY}
            />
          </Box>
        </LockedDeposit>

        <LockedDeposit locked={depositBDisabled} mb="8px">
          <CurrencyInputPanel
            showUSDPrice
            maxAmount={maxAmounts[Field.CURRENCY_B]}
            onMax={() => onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')}
            onPercentInput={(percent) =>
              onFieldBInput(maxAmounts[Field.CURRENCY_B]?.multiply(new Percent(percent, 100)).toExact() ?? '')
            }
            disableCurrencySelect
            value={formattedAmounts[Field.CURRENCY_B]}
            onUserInput={onFieldBInput}
            showQuickInputButton
            showMaxButton
            currency={currencies[Field.CURRENCY_B]}
            id="add-liquidity-input-tokenb"
            showCommonBases
            commonBasesType={CommonBasesType.LIQUIDITY}
          />
        </LockedDeposit>
        {autoSwap && autoSwapCalculation.needsSwap && swapLiquidityError && !swapTradeLoading ? (
          <Message variant="danger" mb="16px">
            <MessageText>{swapLiquidityError}</MessageText>
          </Message>
        ) : null}
        {autoSwap && autoSwapCalculation.needsSwap && swapTrade && !swapTradeLoading && !swapLiquidityError ? (
          <SwapTradeDetails
            swapTrade={swapTrade}
            swapAmount={autoSwapCalculation.swapAmount}
            swapFromCurrency={autoSwapCalculation.swapFromCurrency}
            swapToCurrency={autoSwapCalculation.swapToCurrency}
            isLoading={swapTradeLoading}
          />
        ) : null}
      </DynamicSection>
      <HideMedium>{buttons}</HideMedium>

      <RightContainer>
        <AutoColumn gap="16px">
          {noLiquidity && (
            <NoLiquidityMessage
              startPriceTypedValue={startPriceTypedValue}
              onStartPriceInput={onStartPriceInput}
              baseCurrency={baseCurrency}
              invertPrice={invertPrice}
              price={price}
              quoteCurrency={quoteCurrency}
            />
          )}
          <DynamicSection disabled={!feeAmount || invalidPool}>
            <RowBetween mb="8px">
              <PreTitle>{t('Set Price Range')}</PreTitle>
              <RateToggle
                currencyA={baseCurrency}
                handleRateToggle={() => {
                  if (!ticksAtLimit[Bound.LOWER] && !ticksAtLimit[Bound.UPPER]) {
                    onLeftRangeInput((invertPrice ? priceLower : priceUpper?.invert())?.toSignificant(6) ?? '')
                    onRightRangeInput((invertPrice ? priceUpper : priceLower?.invert())?.toSignificant(6) ?? '')
                    onFieldAInput(formattedAmounts[Field.CURRENCY_B] ?? '')
                  }

                  router.replace(
                    {
                      pathname: router.pathname,
                      query: {
                        ...router.query,
                        currency: [currencyIdB, currencyIdA, feeAmount ? feeAmount.toString() : ''],
                      },
                    },
                    undefined,
                    {
                      shallow: true,
                    },
                  )
                }}
              />
            </RowBetween>

            {!noLiquidity && (
              <>
                {price && baseCurrency && quoteCurrency && !noLiquidity && (
                  <AutoRow
                    gap="4px"
                    marginBottom={['24px', '0px']}
                    justifyContent="center"
                    style={{ marginTop: '0.5rem' }}
                  >
                    <Text fontWeight={500} textAlign="center" fontSize={12} color="text1">
                      {t('Current Price')}:
                    </Text>
                    <Text fontWeight={500} textAlign="center" fontSize={12} color="text1">
                      {invertPrice ? price.invert().toSignificant(6) : price.toSignificant(6)}
                    </Text>
                    <Text color="text2" fontSize={12}>
                      {quoteCurrency?.symbol} per {baseCurrency.symbol}
                    </Text>
                  </AutoRow>
                )}
                <LiquidityChartRangeInput
                  zoomLevel={QUICK_ACTION_CONFIGS?.[feeAmount]?.[activeQuickAction]}
                  onBothRangeInput={onBothRangeInput}
                  key={baseCurrency?.wrapped?.address}
                  currencyA={baseCurrency ?? undefined}
                  currencyB={quoteCurrency ?? undefined}
                  feeAmount={feeAmount}
                  ticksAtLimit={ticksAtLimit}
                  price={price ? parseFloat((invertPrice ? price.invert() : price).toSignificant(8)) : undefined}
                  priceLower={priceLower}
                  priceUpper={priceUpper}
                  onLeftRangeInput={onLeftRangeInput}
                  onRightRangeInput={onRightRangeInput}
                  interactive
                />
              </>
            )}
          </DynamicSection>

          <DynamicSection disabled={!feeAmount || invalidPool || (noLiquidity && !startPriceTypedValue)} gap="16px">
            <RangeSelector
              priceLower={priceLower}
              priceUpper={priceUpper}
              getDecrementLower={getDecrementLower}
              getIncrementLower={getIncrementLower}
              getDecrementUpper={getDecrementUpper}
              getIncrementUpper={getIncrementUpper}
              onLeftRangeInput={onLeftRangeInput}
              onRightRangeInput={onRightRangeInput}
              currencyA={baseCurrency}
              currencyB={quoteCurrency}
              feeAmount={feeAmount}
              ticksAtLimit={ticksAtLimit}
            />
            {showCapitalEfficiencyWarning ? (
              <CapitalEfficiencyWarning
                setShowCapitalEfficiencyWarning={setShowCapitalEfficiencyWarning}
                getSetFullRange={getSetFullRange}
              />
            ) : (
              <Flex justifyContent="space-between" width="100%" style={{ gap: '8px' }}>
                {QUICK_ACTION_CONFIGS[feeAmount] &&
                  Object.entries<ZoomLevels>(QUICK_ACTION_CONFIGS[feeAmount])
                    ?.sort(([a], [b]) => +a - +b)
                    .map(([quickAction, zoomLevel]) => {
                      return (
                        <Button
                          width="100%"
                          key={`quickActions${quickAction}`}
                          onClick={() => {
                            if (+quickAction === activeQuickAction) {
                              handleRefresh(ZOOM_LEVELS[feeAmount])
                              return
                            }
                            handleRefresh(zoomLevel)

                            setActiveQuickAction(+quickAction)
                          }}
                          variant={+quickAction === activeQuickAction ? 'primary' : 'secondary'}
                          scale="sm"
                        >
                          {quickAction}%
                        </Button>
                      )
                    })}
                <Button
                  width="200%"
                  onClick={() => {
                    if (activeQuickAction === 100) {
                      handleRefresh()
                      return
                    }
                    setShowCapitalEfficiencyWarning(true)
                    setActiveQuickAction(100)
                  }}
                  variant={activeQuickAction === 100 ? 'primary' : 'secondary'}
                  scale="sm"
                >
                  {t('Full Range')}
                </Button>
              </Flex>
            )}

            {autoSwap &&
            swapTrade &&
            autoSwapCalculation.finalDepositAmount0 &&
            autoSwapCalculation.finalDepositAmount1 &&
            autoSwapCalculation.swapPercentage ? (
              <Box mt="16px">
                <AutoSwapPreview
                  swapFromCurrency={currencies[independentField]}
                  swapToCurrency={currencies[dependentField]}
                  swapAmount={autoSwapCalculation.swapAmount}
                  swapPercentage={autoSwapCalculation.swapPercentage}
                  finalDepositAmount0={autoSwapCalculation.finalDepositAmount0}
                  finalDepositAmount1={autoSwapCalculation.finalDepositAmount1}
                  token0Currency={autoSwapCalculation.token0Currency}
                  token1Currency={autoSwapCalculation.token1Currency}
                  pool={pool}
                />
              </Box>
            ) : null}

            {outOfRange ? (
              <Message variant="warning">
                <RowBetween>
                  <Text ml="12px" fontSize="12px">
                    {t(
                      'Your position will not earn fees or be used in trades until the market price moves into your range.',
                    )}
                  </Text>
                </RowBetween>
              </Message>
            ) : null}
            {invalidRange ? (
              <Message variant="warning">
                <MessageText>
                  {t('Invalid range selected. The min price must be lower than the max price.')}
                </MessageText>
              </Message>
            ) : null}
          </DynamicSection>
          <MediumOnly>{buttons}</MediumOnly>
        </AutoColumn>
      </RightContainer>
    </>
  )
}
